import React, { useEffect, useState } from 'react';

import { Box, MenuItem, Select, Stack, Typography } from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import { GridRenderEditCellParams, useGridApiContext } from '@mui/x-data-grid';

import { PromoCondition, PromoConditionTypeConfig } from '~/services/PromoService';

import { formatConditionTypeOptionLabel, sortDeprecatedConditionTypes } from '../Vouchers/voucherConditionFormatters';

import { getValidatedConditionApplyType } from './promoConditionTypeUtils';

type Props = {
  params: GridRenderEditCellParams<PromoCondition>;
  promoCodeConditionTypeConfig: Array<PromoConditionTypeConfig>;
  onConditionTypeChange: (rowId: string, newConditionType: string) => void;
};

export default function PromoConditionTypeSelect({
  params,
  promoCodeConditionTypeConfig,
  onConditionTypeChange,
}: Props) {
  const apiRef = useGridApiContext();
  const [currentValue, setCurrentValue] = useState(params.row.condition_type || '');

  useEffect(() => {
    setCurrentValue(params.row.condition_type || '');
  }, [params.row.condition_type]);

  const handleChange = (event: SelectChangeEvent<string>) => {
    const newValue = String(event.target.value);
    setCurrentValue(newValue);
    void apiRef.current.setEditCellValue({ id: params.id, field: 'condition_type', value: newValue });

    const newApplyType = getValidatedConditionApplyType(
      newValue,
      params.row.condition_apply_type,
      promoCodeConditionTypeConfig,
      true,
    );

    void apiRef.current.setEditCellValue({ id: params.id, field: 'condition_apply_type', value: newApplyType });
    onConditionTypeChange(String(params.row.id), newValue);
  };

  const valueOptions = promoCodeConditionTypeConfig
    .map((ct) => ({
      value: ct.key,
      label: formatConditionTypeOptionLabel(ct.key, ct),
      isBeingDeprecated: ct.isBeingDeprecated,
    }))
    .sort(sortDeprecatedConditionTypes);

  const config = params.row.config || promoCodeConditionTypeConfig.find((ct) => ct.key === currentValue);

  return (
    <Stack spacing={1} width="100%">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Select value={currentValue} onChange={handleChange} fullWidth size="small">
          {valueOptions.map((option) => (
            <MenuItem
              key={option.value}
              value={option.value}
              sx={{ textDecoration: option.isBeingDeprecated ? 'line-through' : 'none' }}
            >
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </Box>
      {config?.longDescription && (
        <Box px={1}>
          <Typography variant="body2">
            {config.longDescription}{' '}
            {!config.isAvailableInAPP && (
              <Typography variant="caption" component="span" color="warning.main">
                [Behavior blocked in APP]
              </Typography>
            )}
          </Typography>
        </Box>
      )}
    </Stack>
  );
}
